/* eslint-disable jsx-a11y/control-has-associated-label */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import SVG from 'components/icons/SVG';
import ButtonText from 'components/shared/ButtonText';
import styles from './styles.module.scss';

// NOTE: desktop styles are unfinished, this is only on mobile right now

function ContextMenu(props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ offsetTop: 0, offsetLeft: 0 });
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const isCancelled = useRef(false);

  const toggleMenuOpen = () => setMenuOpen(!menuOpen);

  const closeOnClickOutside = useCallback(
    e => {
      if (menuOpen && !wrapperRef.current?.contains(e.target)) {
        setMenuOpen(false);
      }
    },
    [menuOpen]
  );

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const { top /* , right */, bottom /* , left */ } = buttonRef.current.getBoundingClientRect();
      const containerWidth = wrapperRef.current.offsetWidth;
      if (isCancelled.current) return;
      // modify based on appears above/below
      const newPosition = { top: 0, right: 0, bottom: 0, left: 0 };
      if (props.appearsVertical === 'above') {
        newPosition.bottom = bottom - top - 5;
        newPosition.top = 'auto';
      } else {
        newPosition.top = bottom + 5;
        newPosition.bottom = 'auto';
      }
      newPosition.right = containerWidth / 2 - 14; // 14px to account for size and position of caret
      newPosition.left = 'auto';
      // TODO: make more general so we can use it elsewhere
      // Need to center the arrow on the menu item
      // if (props.appearsHorizontal === 'left') {
      //   newPosition.right = window.innerWidth - right;
      //   newPosition.left = 'auto';
      // } else {
      //   newPosition.left = left;
      //   newPosition.right = 'auto';
      // }
      setMenuPosition(newPosition);
    }
  }, [buttonRef /* , props.appearsHorizontal, */, props.appearsVertical]);

  useEffect(() => {
    updatePosition();
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });
    window.addEventListener('click', closeOnClickOutside, { passive: true });

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('click', closeOnClickOutside);
    };
  }, [menuOpen, updatePosition, closeOnClickOutside]);

  useEffect(() => {
    return () => (isCancelled.current = true);
  }, []);

  return (
    <div className={classnames([styles.container, ...props.containerStyles])} ref={wrapperRef}>
      <button
        type="button"
        onClick={toggleMenuOpen}
        className={classnames([styles.button, ...props.buttonStyles])}
        ref={buttonRef}>
        <SVG icon="options" />
      </button>
      {menuOpen && (
        <div
          className={classnames([
            styles.menu,
            {
              [styles.menuAbove]: props.appearsVertical === 'above',
              [styles.menuBelow]: props.appearsVertical === 'below',
              [styles.menuLeft]: props.appearsHorizontal === 'left',
              [styles.menuRight]: props.appearsHorizontal === 'right'
            },
            ...props.menuStyles
          ])}
          style={{
            ...menuPosition
          }}>
          {props.options.map(({ label, ...rest }) => {
            return (
              <ButtonText {...rest} key={label} styles={[styles.optionButton, ...props.optionButtonStyles]}>
                {label}
              </ButtonText>
            );
          })}
        </div>
      )}
    </div>
  );
}

ContextMenu.propTypes = {
  options: PropTypes.array,
  appearsVertical: PropTypes.oneOf(['above', 'below']),
  appearsHorizontal: PropTypes.oneOf(['left', 'right']),
  containerStyles: PropTypes.array,
  buttonStyles: PropTypes.array,
  menuStyles: PropTypes.array,
  optionButtonStyles: PropTypes.array
};

ContextMenu.defaultProps = {
  options: [],
  appearsVertical: 'below',
  appearsHorizontal: 'right',
  containerStyles: [],
  buttonStyles: [],
  menuStyles: [],
  optionButtonStyles: []
};

export default ContextMenu;
